import { z } from "zod";

import type { InternalUserResponse } from "../types/users-api.types";

/**
 * Digits only, 6–15 of them.
 *
 * A deliberate SUBSET of what the API accepts: the backend runs `libphonenumber` over `"+" + value`, so
 * anything failing this regex certainly fails there too, while a number this allows may still be rejected
 * server-side. Erring that way is the point — a client rule stricter than the server blocks legitimate input,
 * which is worse than a round trip. What it does catch is the common mistake the hint warns about: typing the
 * `+` or spacing the number.
 *
 * Exported so the create drawer enforces the identical rule. It used to accept any string, so the same input
 * the edit drawer caught client-side came back from the API as a raw `class-validator` sentence.
 */
export const PHONE_PATTERN = /^\d{6,15}$/;

export interface UserProfileFormMessages {
  required: string;
  email: string;
  phone: string;
}

/**
 * The fields of `UpdateUserDto` the detail drawer edits.
 *
 * Lengths mirror the backend's validators (`@MinLength(2) @MaxLength(255)` on the name, `@MaxLength(255)` on
 * the email, `@MaxLength(100)` on the country) so the operator is told before the round trip rather than
 * after it. In particular `full_name` is required here: the diff builder drops a falsy name, so without this
 * rule clearing the name would silently do nothing instead of reporting a problem.
 *
 * Optional strings validate as "empty OR valid" rather than being marked optional, because an emptied field
 * is a real instruction: it CLEARS the value server-side, and `buildUserDiff` turns `""` into `null`.
 */
export function buildUserProfileSchema(messages: UserProfileFormMessages) {
  return z.object({
    full_name: z.string().trim().min(2, messages.required).max(255),
    email: z.string().trim().min(1, messages.required).email(messages.email).max(255),
    country: z.string().trim().max(100),
    phone_number: z
      .string()
      .trim()
      .refine((value) => value === "" || PHONE_PATTERN.test(value), messages.phone),
    preferred_language: z.string(),
  });
}

export type UserProfileFormValues = z.output<ReturnType<typeof buildUserProfileSchema>>;

/** Maps a loaded user onto the form's defaults. `null` becomes `""` so an untouched field reads as unchanged. */
export function toUserProfileFormValues(user: InternalUserResponse): UserProfileFormValues {
  return {
    full_name: user.full_name,
    email: user.email,
    country: user.country ?? "",
    phone_number: user.phone_number ?? "",
    preferred_language: user.preferred_language ?? "",
  };
}

export const EMPTY_USER_PROFILE_FORM: UserProfileFormValues = {
  full_name: "",
  email: "",
  country: "",
  phone_number: "",
  preferred_language: "",
};
